import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { webDtos } from '@vectronic/openApi-admin';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, TableCollapseService } from '@vectronic/util';

@Component({
  selector: 'table-data-forwarding-chips-cell',
  templateUrl: './data-forwarding-chips-cell.component.html',
  styleUrls: ['./data-forwarding-chips-cell.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
})
export class DataForwardingChipsCellComponent {
  private dialogService = inject(DialogService);
  private tableCollapseService = inject(TableCollapseService);

  collarId = input<number>();
  selectedDataForwarders = input<webDtos.DataForwardingRule[]>();
  dataForwarders = input<webDtos.DataForwardingRule[]>();
  readonly = input<boolean>();
  isCompactModeActivated = input<boolean>();

  addDataForwarder = output<webDtos.DataForwardingRule>();
  deleteDataForwarder = output<webDtos.DataForwardingRule>();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  data: webDtos.DataForwardingRule[];

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  formControl = new FormControl('');
  inputSignal = toSignal(this.formControl.valueChanges, { initialValue: '' });

  filteredDataForwarders = computed(() =>
    this.inputSignal()
      ? this._filter(this.inputSignal(), this.dataForwarders())
      : this.dataForwarders()?.slice(),
  );

  add(event: MatChipInputEvent) {
    const value = (event.value || '')?.trim();
    this.addDataForwarder.emit({
      destinationEmailAddress: value,
      deviceId: this.collarId(),
    });
    event.chipInput?.clear();
    this.formControl.setValue(null);
    if (this.input?.nativeElement.value !== undefined) {
      this.input.nativeElement.value = '';
    }
  }

  addButton() {
    this.add({
      input: this.input?.nativeElement,
      value: this.formControl.value,
      chipInput: undefined,
    });
  }

  onRemoveRule(rule: webDtos.DataForwardingRule) {
    this.dialogService.open(ConfirmationDialogComponent, {
      afterConfirm: () => this.deleteDataForwarder.emit(rule),
    });
  }

  selected(event: MatAutocompleteSelectedEvent) {
    const dataForwarder = this.data.find(
      (item) => item.destinationEmailAddress === event.option.value,
    );
    if (dataForwarder) {
      this.addDataForwarder.emit(dataForwarder);
    }
    this.formControl.setValue(null);
    this.input.nativeElement.value = '';
  }

  private _filter(value: string, data: webDtos.DataForwardingRule[]) {
    if (typeof value !== 'string') return data.slice();
    const filterValue = value.toLowerCase();
    return data?.filter((address) =>
      address.destinationEmailAddress.toLowerCase().includes(filterValue),
    );
  }

  onClickShowMore() {
    if (!this.showMore) {
      this.tableCollapseService.setDataForwarerSelectedCollarId(this.collarId());
      setTimeout(() => {
        this.input?.nativeElement?.focus();
      }, 400);
    }
  }

  onClickShowLess(event: MouseEvent) {
    event.stopPropagation();
    if (this.showMore) {
      this.tableCollapseService.setDataForwarerSelectedCollarId(undefined);
    }
  }

  get showMore() {
    return this.tableCollapseService.isDataForwarderSelectedCollarId(this.collarId());
  }
}
